import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Tag, ArrowUpCircle, ArrowRight, X } from "lucide-react";

const fmtDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };
const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Choose which brand a new subscription is for; app-only brands can be added inline.
export function BrandPickerDialog({ appId, plan, brands, takenIds, workspaceName, onClose, onPick, onBrandAdded, onBrandRemoved }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const options = [{ id: "", name: workspaceName || "Main account", kind: "main" }, ...brands];
  const add = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/apps/${appId}/brands`, { name });
      onBrandAdded(data); onPick(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSaving(false); }
  };
  const remove = async (b) => {
    try { await api.delete(`/apps/${appId}/brands/${b.id}`); onBrandRemoved(b.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="brand-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Which brand is {plan.name} for?</DialogTitle>
          <DialogDescription>Each brand gets its own subscription you can cancel separately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[45vh] overflow-y-auto">
          {options.map((b) => {
            const taken = takenIds.includes(b.id);
            return (
              <div key={b.id || "main"} className="flex items-center gap-1.5">
              <button data-testid={`brand-option-${b.id || "main"}`} disabled={taken} onClick={() => onPick(b)}
                className="w-full flex items-center gap-3 rounded-xl border border-[#EEEFEF] px-3.5 py-2.5 text-left hover:border-[#723FFF] hover:bg-[#F7F4FF] transition-colors disabled:opacity-70 disabled:bg-[#FAFAFD] disabled:hover:border-[#EEEFEF] disabled:hover:bg-white disabled:cursor-not-allowed">
                <Tag className="h-4 w-4 text-[#723FFF] shrink-0" />
                <span className="flex-1 text-sm text-[#06060C] truncate">{b.name}</span>
                <span className={`font-mono-data text-[9px] uppercase tracking-wider ${taken ? "text-[#1f9d55]" : "text-[#9FA1A4]"}`}>{taken ? "Already subscribed" : b.kind === "app" ? "App only" : b.kind === "main" ? "Account" : "Brand"}</span>
              </button>
              {b.kind === "app" && !taken && <button data-testid={`brand-remove-${b.id}`} title="Remove this app-only brand" onClick={() => remove(b)} className="h-8 w-8 shrink-0 flex items-center justify-center rounded-lg text-[#c4c5cc] hover:text-rose-600 hover:bg-rose-50"><X className="h-4 w-4" /></button>}
              </div>
            );
          })}
        </div>
        {!adding ? (
          <button data-testid="brand-add-toggle" onClick={() => setAdding(true)} className="text-sm text-[#723FFF] hover:text-[#5b2fd6] flex items-center gap-1.5"><Plus className="h-4 w-4" /> Not a workspace brand? Add one for this app</button>
        ) : (
          <div className="rounded-xl border border-dashed border-[#d9ccff] bg-[#F7F4FF] p-3 space-y-2">
            <p className="text-[11px] text-[#595D62]">Only used for this app's subscriptions. It won't be added to your workspace brands.</p>
            <div className="flex gap-2">
              <Input autoFocus data-testid="brand-add-input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && add()} placeholder="Brand name" className="bg-white border-[#EEEFEF]" />
              <Button data-testid="brand-add-save" disabled={saving || !name.trim()} onClick={add} className="rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24] shrink-0">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4 ml-1" /></>}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function BrandSubRow({ sub, upgrades, busy, onCancel, onResume, onUpgrade }) {
  return (
    <div data-testid={`brand-sub-${sub.id}`} className="rounded-xl border border-[#723FFF]/25 bg-white px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-[#06060C] truncate flex items-center gap-1.5" data-testid={`brand-sub-name-${sub.id}`}><Tag className="h-3.5 w-3.5 text-[#723FFF]" />{sub.brand_name}</span>
        <span className={`text-[11px] ${sub.cancel_at_period_end ? "text-[#b7791f]" : "text-[#9FA1A4]"}`}>{sub.cancel_at_period_end ? `Cancels ${fmtDate(sub.current_period_end)}` : `Renews ${fmtDate(sub.current_period_end)}`}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sub.cancel_at_period_end ? (
          <Button size="sm" data-testid={`resume-btn-${sub.id}`} onClick={() => onResume(sub)} disabled={busy} variant="outline" className="h-7 rounded-full border-[#EEEFEF] text-xs">Resume</Button>
        ) : (
          <>
            {upgrades.map((t) => (
              <Button key={t.id} size="sm" data-testid={`upgrade-btn-${sub.id}-${t.id}`} onClick={() => onUpgrade(sub, t)} disabled={busy} className="h-7 rounded-full bg-[#723FFF] text-white hover:bg-[#5b2fd6] text-xs"><ArrowUpCircle className="h-3.5 w-3.5 mr-1" /> Upgrade to {t.name} ({money(t.price)}/mo)</Button>
            ))}
            <Button size="sm" data-testid={`cancel-btn-${sub.id}`} onClick={() => onCancel(sub)} disabled={busy} variant="outline" className="h-7 rounded-full border-[#EEEFEF] text-[#595D62] text-xs">Cancel</Button>
          </>
        )}
      </div>
    </div>
  );
}
